import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from '../axios';
import { RootState } from '../store';
import StatusModal from './StatusModal';

interface Rubric {
  id: number;
  name: string;
  description: string;
}

const RubricSelection: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const currentLanguage = useSelector((state: RootState) => state.language.currentLanguage);
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [selectedRubrics, setSelectedRubrics] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOnboarding, setIsOnboarding] = useState(false);

  // Simple inline localization helper
  const getText = (key: string) => {
    const texts: { [key: string]: { de: string; en: string } } = {
      'onboardingTitle': { de: 'Wählen Sie Ihre Interessen', en: 'Choose Your Interests' },
      'onboardingSubtitle': { de: 'Wählen Sie die Themen aus, die Sie am meisten interessieren, um Ihr Erlebnis zu personalisieren', en: 'Select the topics that interest you most to personalize your experience' },
      'manageTitle': { de: 'Ihre Rubriken verwalten', en: 'Manage Your Rubrics' },
      'manageSubtitle': { de: 'Ihre Rubrik-Präferenzen verwalten', en: 'Manage your rubric preferences' },
      'loading': { de: 'Rubriken werden geladen...', en: 'Loading rubrics...' },
      'continueWithSelected': { de: 'Mit ausgewählten Themen fortfahren', en: 'Continue with Selected Topics' },
      'skipForNow': { de: 'Vorerst überspringen', en: 'Skip for Now' },
      'saveChanges': { de: 'Änderungen speichern', en: 'Save Changes' },
      'needSubscription': { de: 'Sie benötigen ein Premium-Abonnement, um Rubriken zu verwalten.', en: 'You need a premium subscription to manage rubrics.' }
    };
    return texts[key]?.[currentLanguage === 'de' ? 'de' : 'en'] || key;
  };

  // Check if user has admin or paid access (keep existing logic)
  const hasAccess = user?.roles?.includes('PERMISSION_ADMIN') || 
                   user?.roles?.includes('PERMISSION_PAID_ACCESS');

  // Allow access during onboarding OR if user has existing permissions
  // This way we don't change existing permission logic, just add onboarding exception
  const allowRubricSelection = hasAccess || isOnboarding;

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    type: 'success' | 'error';
  }>({
    isOpen: false,
    type: 'success'
  });

  useEffect(() => {
    // Check if user is in onboarding (new user from signup)
    const urlParams = new URLSearchParams(window.location.search);
    const onboarding = urlParams.get('onboarding') === 'true' || window.location.pathname === '/rubric-selection';
    setIsOnboarding(onboarding);

    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch all available rubrics
        const rubricsRes = await axios.get('/api/rubrics');
        setRubrics(rubricsRes.data);

        // Fetch user's selected rubrics if user exists and not in onboarding
        if (user?.id && !onboarding) {
          try {
            const selectedRes = await axios.get(`/api/admin/users/${user.id}/selected-rubrics`);
            const selectedIds = selectedRes.data.map((rubric: Rubric) => rubric.id);
            setSelectedRubrics(selectedIds);
          } catch (err) {
            // If fetching fails, start with empty selection (for new users)
            console.log('No existing rubric selection found, starting fresh');
          }
        }
      } catch (err) {
        setError('Could not load rubrics');
        console.error('Error fetching rubrics:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id]); // Only re-run if user ID changes

  const handleToggleRubric = (rubricId: number) => {
    if (selectedRubrics.includes(rubricId)) {
      setSelectedRubrics(selectedRubrics.filter(id => id !== rubricId));
    } else {
      setSelectedRubrics([...selectedRubrics, rubricId]);
    }
  };

 const handleSave = async () => {
    if (!user) return;
    try {
      await axios.put(`/api/admin/users/${user.id}/rubrics`, selectedRubrics);
      if (isOnboarding) {
        // For onboarding, navigate to main page after successful save
        navigate('/');
      } else {
        // For existing users, show success modal
        setModalState({
          isOpen: true,
          type: 'success'
        });
      }
    } catch (err) {
      console.error(err);
      setModalState({
        isOpen: true,
        type: 'error'
      });
    }
  };

  const handleSkipOnboarding = () => {
    // Allow users to skip rubric selection and go to main page
    navigate('/');
  };

  // Check if we're in standalone mode (full page) or embedded mode (in profile)
  const isStandalone = isOnboarding || window.location.pathname === '/rubric-selection';

  if (isStandalone) {
    // Full page layout for onboarding/standalone
    return (
      <div className="min-h-screen bg-[#CAD4DD] flex items-center justify-center p-4">
        <div className="max-w-4xl w-full bg-white rounded-lg shadow-lg p-8">
          <div className="space-y-6 text-black">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-[#49ABDB] mb-2">
                {getText('onboardingTitle')}
              </h2>
              <p className="text-gray-600">
                {getText('onboardingSubtitle')}
              </p>
            </div>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="text-gray-500">{getText('loading')}</div>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <div className="text-red-500">{error}</div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {rubrics.map(rubric => (
                    <label
                      key={rubric.id}
                      className={`flex items-start space-x-3 p-4 rounded-lg border-2 transition-all ${
                        selectedRubrics.includes(rubric.id)
                          ? 'border-[#49ABDB] bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      } ${
                        allowRubricSelection ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed bg-gray-100'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRubrics.includes(rubric.id)}
                        onChange={() => handleToggleRubric(rubric.id)}
                        disabled={!allowRubricSelection}
                        className="h-5 w-5 text-[#49ABDB] focus:ring-[#49ABDB] border-gray-300 rounded mt-0.5 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-black break-words">{rubric.name}</div>
                        {rubric.description && (
                          <div className="text-sm text-gray-600 mt-1 break-words leading-relaxed">
                            {rubric.description}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
                  <button
                    onClick={handleSave}
                    disabled={selectedRubrics.length === 0}
                    className="px-6 py-3 bg-gradient-to-r from-[#49ABDB] to-[#A0C9DA] text-white font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {getText('continueWithSelected')} ({selectedRubrics.length})
                  </button>
                  <button
                    onClick={handleSkipOnboarding}
                    className="px-6 py-3 bg-gray-200 text-gray-700 font-bold hover:bg-gray-300 transition-colors"
                  >
                    {getText('skipForNow')}
                  </button>
                </div>
              </>
            )}
          </div>
          
          <StatusModal 
            isOpen={modalState.isOpen}
            onClose={() => setModalState(prev => ({ ...prev, isOpen: false }))}
            type={modalState.type}
          />
        </div>
      </div>
    );
  }

  // Embedded layout for profile page
  return (
    <div className="space-y-6 text-black">
      <div>
        <h2 className="text-xl font-bold text-[#111D4E] mb-2">
          {getText('manageTitle')}
        </h2>
        <p className="text-gray-600">
          {getText('manageSubtitle')}
        </p>
      </div>
      
      {loading ? (
        <div className="text-center py-8">
          <div className="text-gray-500">{getText('loading')}</div>
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <div className="text-red-500">{error}</div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {rubrics.map(rubric => (
              <label
                key={rubric.id}
                className={`flex items-start space-x-3 p-4 bg-white rounded-lg border-2 transition-all ${
                  selectedRubrics.includes(rubric.id)
                    ? 'border-[#49ABDB] bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                } ${
                  allowRubricSelection ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed bg-gray-100'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedRubrics.includes(rubric.id)}
                  onChange={() => handleToggleRubric(rubric.id)}
                  disabled={!allowRubricSelection}
                  className="h-5 w-5 text-[#49ABDB] focus:ring-[#49ABDB] border-gray-300 rounded mt-0.5 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-black break-words">{rubric.name}</div>
                  {rubric.description && (
                    <div className="text-sm text-gray-600 mt-1 break-words leading-relaxed">
                      {rubric.description}
                    </div>
                  )}
                </div>
              </label>
            ))}
          </div>
          
          <div className="flex justify-start pt-4">
            <button
              onClick={handleSave}
              disabled={!allowRubricSelection}
              className={`px-6 py-3 font-bold transition-opacity ${
                allowRubricSelection
                  ? 'bg-gradient-to-r from-[#49ABDB] to-[#A0C9DA] text-white hover:opacity-90'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {getText('saveChanges')}
            </button>
          </div>
          
          {!allowRubricSelection && (
            <p className="text-gray-500 text-sm">
              {getText('needSubscription')}
            </p>
          )}
        </>
      )}
      
      <StatusModal 
        isOpen={modalState.isOpen}
        onClose={() => setModalState(prev => ({ ...prev, isOpen: false }))}
        type={modalState.type}
      />
    </div>
  );
};

export default RubricSelection;